// Shared redemption status chip + timeline (plan §12). Presentation only; it renders the
// authoritative server state and keeps review time distinct from bank delivery time.
import React from "react";
import { REQUEST_STATE_TONE, stateLabel } from "@/lib/payouts";

// Palette matches the admin CashTab status chip: green = positive/progress, blue = sent/
// processing, red = negative, neutral gray = waiting (NO amber — on-system per design).
const TONE = {
  ok: { fg: "#00D632", bg: "rgba(0,214,50,0.12)" },
  info: { fg: "#60A5FA", bg: "rgba(59,130,246,0.14)" },
  warn: { fg: "#d4d4d8", bg: "#26262B" },
  bad: { fg: "#EF4444", bg: "rgba(239,68,68,0.15)" },
  neutral: { fg: "#d4d4d8", bg: "#26262B" },
};

export function RedemptionStatusChip({ state }) {
  const tone = TONE[REQUEST_STATE_TONE[state] || "neutral"];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold"
      style={{ color: tone.fg, backgroundColor: tone.bg }}
    >
      {stateLabel(state)}
    </span>
  );
}

// The public-facing lifecycle, in order. Review and bank delivery are separate steps
// so a player never reads an approval as a guaranteed settlement.
const STEPS = [
  { key: "received", label: "Received" },
  { key: "review", label: "Under review" },
  { key: "approved", label: "Approved" },
  { key: "processing", label: "Bank processing" },
  { key: "done", label: "Paid" },
];

function reachedIndex(state) {
  switch (state) {
    case "under_review":
    case "awaiting_information":
    case "on_hold":
      return 1;
    case "approved":
    case "queued":
      return 2;
    case "processing":
      return 3;
    case "paid":
    case "returned":
      return 4;
    default:
      return 0; // rejected/cancelled/failed — no forward progress
  }
}

export function RedemptionTimeline({ state }) {
  const reached = reachedIndex(state);
  const terminalBad = ["rejected", "cancelled", "failed"].includes(state);
  return (
    <ol className="flex flex-col gap-2" aria-label="Redemption progress">
      {STEPS.map((step, i) => {
        const active = i <= reached && !terminalBad;
        return (
          <li key={step.key} className="flex items-center gap-2 text-[13px]">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: active ? "#00D632" : "#3F3F46" }}
            />
            <span style={{ color: active ? "#e4e4e7" : "#71717a" }}>{step.label}</span>
          </li>
        );
      })}
      {terminalBad && (
        <li className="text-[12px]" style={{ color: "#EF4444" }}>
          {stateLabel(state)} — reserved funds have been returned to your balance.
        </li>
      )}
    </ol>
  );
}
